import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChartData, ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';

import { MobilePageShellComponent } from '../../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { MobileDataCardComponent, MobileCardField } from '../../../shared/mobile/mobile-data-card/mobile-data-card.component';
import { MobileMetricRibbonComponent } from '../../../shared/mobile/mobile-metric-ribbon/mobile-metric-ribbon.component';
import { SingleSelectDropdownComponent } from '../../business-details/single-select-dropdown.component';
import { AivorInsightsComponent } from '../aivor-insights/aivor-insights.component';
import { BudgetItemOffcanvasComponent, BudgetFormPayload } from './budget-item-offcanvas/budget-item-offcanvas.component';

@Component({
  selector: 'app-mobile-costing-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgChartsModule,
    MobilePageShellComponent,
    MobileDataCardComponent,
    MobileMetricRibbonComponent,
    SingleSelectDropdownComponent,
    AivorInsightsComponent,
    BudgetItemOffcanvasComponent,
  ],
  templateUrl: './mobile-costing-view.component.html',
  styleUrls: ['./mobile-costing-view.component.css'],
})
export class MobileCostingViewComponent implements OnInit, OnDestroy {
  // --- Data inputs ---
  @Input() filteredBudgets: any[] = [];
  @Input() actualCostByBudgetId: Record<string, number> = {};
  @Input() fiscalPeriods: any[] = [];
  @Input() items: any[] = [];
  @Input() isLoading = false;

  // Filter state (two-way mirror from parent)
  @Input() selectedBudgetType = 'monthly';
  @Input() selectedMonth: number = new Date().getMonth();
  @Input() selectedQuarter: number = Math.floor(new Date().getMonth() / 3) + 1;
  @Input() selectedYear: number = new Date().getFullYear();
  @Input() availableMonths: { value: number; label: string }[] = [];
  @Input() availableQuarters: { value: number; label: string }[] = [];
  @Input() availableYears: number[] = [];
  @Input() dateRange: { start: Date | null; end: Date | null } = { start: null, end: null };

  // Chart data passed from parent (already computed)
  @Input() budgetVsActualChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  @Input() currentPage = 1;
  @Input() totalPages = 1;

  // --- Filter change outputs ---
  @Output() budgetTypeChange = new EventEmitter<string>();
  @Output() monthChange = new EventEmitter<number>();
  @Output() quarterChange = new EventEmitter<number>();
  @Output() yearChange = new EventEmitter<number>();

  // --- CRUD outputs ---
  @Output() saveBudget = new EventEmitter<BudgetFormPayload>();
  @Output() deleteBudget = new EventEmitter<string>();
  @Output() prevPage = new EventEmitter<void>();
  @Output() nextPage = new EventEmitter<void>();

  // --- Active view (from ?view= query param) ---
  activeView: 'overview' | 'budgets' | 'ai' = 'overview';
  private sub = new Subscription();

  // --- Inner tab state ---
  activeInnerTab: 'chart' | 'records' = 'records';
  setInnerTab(t: 'chart' | 'records') { this.activeInnerTab = t; }

  // --- Filter collapse state ---
  budgetFiltersOpen = false;
  toggleBudgetFilters(): void { this.budgetFiltersOpen = !this.budgetFiltersOpen; }

  get activeFilterCount(): number {
    let count = 0;
    if (this.selectedBudgetType !== 'all') count++;
    if (this.selectedBudgetType === 'monthly' || this.selectedBudgetType === 'quarterly' || this.selectedBudgetType === 'annually') count++;
    return count;
  }

  // --- Offcanvas state ---
  offcanvasVisible = false;
  offcanvasEditing = false;
  offcanvasBudget: any = null;

  // --- Delete sheet state ---
  deleteSheetVisible = false;
  deleteSheetClosing = false;
  budgetToDelete: any = null;
  mobileDeleteSwipeDeltaY = 0;
  mobileDeleteSwipeProgress = 0;
  mobileDeleteSwipeActive = false;
  private mobileDeleteSwipeStartY = 0;
  private mobileDeleteSwipeSheetHeight = 0;

  readonly Math = Math;
  readonly String = String;

  get budgetTypeOptions() {
    return [
      { value: 'all',       label: 'All Types' },
      { value: 'monthly',   label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
      { value: 'annually',  label: 'Annually' },
      { value: 'per-item',  label: 'Per Item' },
    ];
  }
  get monthOptions()   { return this.availableMonths.map(m  => ({ value: String(m.value), label: m.label })); }
  get quarterOptions() { return this.availableQuarters.map(q => ({ value: String(q.value), label: q.label })); }
  get yearOptions()    { return this.availableYears.map(y   => ({ value: String(y),       label: String(y) })); }

  onTypeDropdown(v: string | null)    { this.budgetTypeChange.emit(v ?? 'all'); }
  onMonthDropdown(v: string | null)   { this.monthChange.emit(v ? +v : null!); }
  onQuarterDropdown(v: string | null) { this.quarterChange.emit(v ? +v : null!); }
  onYearDropdown(v: string | null)    { this.yearChange.emit(v ? +v : null!); }

  readonly mobileChartOptions: ChartConfiguration['options'] = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx: any) => `R ${(ctx.raw as number).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` } },
    },
    scales: {
      x: { ticks: { callback: (v: any) => `R${Number(v).toLocaleString('en-ZA')}`, font: { size: 10 } } },
      y: { ticks: { font: { size: 10 } } },
    },
  };

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.sub.add(
      this.route.queryParams.subscribe(params => {
        const v = params['view'];
      this.activeView = v === 'budgets' ? 'budgets' : v === 'ai' ? 'ai' : 'overview';
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // --- Metric ribbon ---
  get totalBudgetAmount(): number {
    return this.filteredBudgets.reduce((sum, b) => sum + this.calcPlanned(b), 0);
  }

  get totalActualAmount(): number {
    return this.filteredBudgets.reduce((sum, b) => sum + (this.actualCostByBudgetId[b.budgetID] ?? 0), 0);
  }

  get varianceAmount(): number {
    return this.totalBudgetAmount - this.totalActualAmount;
  }

  get ribbonMetrics() {
    return [
      { label: 'Total Budget', value: this.formatCurrency(this.totalBudgetAmount) },
      { label: 'Total Actual', value: this.formatCurrency(this.totalActualAmount) },
      { label: 'Variance', value: this.formatCurrency(this.varianceAmount), warning: this.varianceAmount < 0 },
    ];
  }

  // --- Card helpers ---
  calcPlanned(budget: any): number {
    return (budget.lineItems ?? []).reduce((s: number, l: any) => s + (Number(l.budgetAmount) || 0), 0);
  }

  cardFields(budget: any): MobileCardField[] {
    const planned = this.calcPlanned(budget);
    const actual = this.actualCostByBudgetId[budget.budgetID] ?? 0;
    const variance = planned - actual;
    return [
      { label: 'Planned', value: this.formatCurrency(planned), chip: true },
      { label: 'Actual', value: this.formatCurrency(actual), chip: true },
      { label: 'Variance', value: this.formatCurrency(variance), chip: true, tone: variance < 0 ? 'danger' : 'success' },
    ];
  }

  // --- Offcanvas open/close ---
  openAdd(): void {
    this.offcanvasBudget = null;
    this.offcanvasEditing = false;
    this.offcanvasVisible = true;
  }

  openEdit(budget: any): void {
    this.offcanvasBudget = budget;
    this.offcanvasEditing = true;
    this.offcanvasVisible = true;
  }

  onOffcanvasClose(): void {
    this.offcanvasVisible = false;
    this.offcanvasBudget = null;
  }

  onOffcanvasSave(payload: BudgetFormPayload): void {
    this.saveBudget.emit(payload);
  }

  // --- Delete sheet ---
  openDeleteSheet(budget: any): void {
    this.budgetToDelete = budget;
    this.deleteSheetClosing = false;
    this.deleteSheetVisible = true;
    this.mobileDeleteSwipeDeltaY = 0;
    this.mobileDeleteSwipeProgress = 0;
  }

  closeDeleteSheet(): void {
    this.deleteSheetClosing = true;
    setTimeout(() => {
      this.deleteSheetClosing = false;
      this.deleteSheetVisible = false;
      this.budgetToDelete = null;
      this.mobileDeleteSwipeDeltaY = 0;
      this.mobileDeleteSwipeProgress = 0;
      this.mobileDeleteSwipeActive = false;
    }, 300);
  }

  confirmDelete(): void {
    if (!this.budgetToDelete) return;
    const id = this.budgetToDelete.budgetID;
    this.closeDeleteSheet();
    this.deleteBudget.emit(id);
  }

  onDeleteSheetTouchStart(ev: TouchEvent): void {
    this.mobileDeleteSwipeStartY = ev.touches[0].clientY;
    this.mobileDeleteSwipeSheetHeight = (ev.currentTarget as HTMLElement).closest('.m-del-sheet')?.clientHeight || 300;
    this.mobileDeleteSwipeActive = true;
  }

  onDeleteSheetTouchMove(ev: TouchEvent): void {
    const delta = ev.touches[0].clientY - this.mobileDeleteSwipeStartY;
    if (delta < 0) { this.mobileDeleteSwipeDeltaY = 0; this.mobileDeleteSwipeProgress = 0; return; }
    this.mobileDeleteSwipeDeltaY = delta;
    this.mobileDeleteSwipeProgress = Math.min(1, delta / (this.mobileDeleteSwipeSheetHeight * 0.3));
  }

  onDeleteSheetTouchEnd(): void {
    this.mobileDeleteSwipeActive = false;
    if (this.mobileDeleteSwipeProgress >= 1) {
      this.closeDeleteSheet();
      return;
    }
    this.mobileDeleteSwipeDeltaY = 0;
    this.mobileDeleteSwipeProgress = 0;
  }

  // --- Utilities ---
  formatCurrency(value: number): string {
    return 'R ' + value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
